import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  ButtonGroup,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useState } from 'react';
import type { ManagementTimelineQuery } from '@internal/spassu-code-contract';
import type { CockpitTimelineState } from '../controller/cockpitControllerTypes';
import type { CockpitViewModel } from '../model/cockpitViewModel';
import { TimelineFilters } from './TimelineFilters';
import { isTimelineLoadingMore, TimelineLoadState } from './TimelineLoadState';
import { TimelineMarkerCard } from './TimelineMarkerCard';
import { useCockpitStyles } from './cockpitStyles';
import {
  changeTimelineView,
  clearTimelineGroup,
  expandTimelineGroup,
} from './timelineFilterState';

export interface TimelineSectionProps {
  timeline: CockpitTimelineState;
  model: CockpitViewModel;
  selectedId: string | null;
  onSelect(markerId: string): void;
  onQueryChange(query: ManagementTimelineQuery): void;
  onLoadMore(): void;
}

const TimelineModeControls = ({
  query,
  onChange,
  filtersOpen,
  onToggleFilters,
}: {
  query: ManagementTimelineQuery;
  onChange(query: ManagementTimelineQuery): void;
  filtersOpen: boolean;
  onToggleFilters(): void;
}) => {
  const classes = useCockpitStyles();
  const view = query.view ?? 'management';
  return (
    <div className={classes.timelineToolbar}>
      <ButtonGroup aria-label="Modo da timeline">
        {(['management', 'technical'] as const).map(value => (
          <Button
            key={value}
            aria-pressed={view === value}
            variant={view === value ? 'contained' : 'outlined'}
            onClick={() => onChange(changeTimelineView(query, value))}
          >
            {value === 'management' ? 'Gerencial' : 'Técnica'}
          </Button>
        ))}
      </ButtonGroup>
      <div className={classes.timelineToolbarActions}>
        {query.groupKey && (
          <Button onClick={() => onChange(clearTimelineGroup(query))}>
            Voltar aos marcos
          </Button>
        )}
        <Button
          variant="outlined"
          aria-expanded={filtersOpen}
          aria-controls="timeline-filters"
          onClick={onToggleFilters}
        >
          {filtersOpen ? 'Ocultar filtros' : 'Filtrar'}
        </Button>
      </div>
    </div>
  );
};

const TimelineMarkerList = ({
  timeline,
  model,
  selectedId,
  onSelect,
  onQueryChange,
}: Pick<
  TimelineSectionProps,
  'timeline' | 'model' | 'selectedId' | 'onSelect' | 'onQueryChange'
>) => {
  const classes = useCockpitStyles();
  const { page, query } = timeline;
  if (!page || page.markers.length === 0) return null;
  const groups = new Map(page.groups.map(group => [group.key, group]));
  return (
    <ol className={classes.timelineList} aria-label="Timeline da atividade">
      {page.markers.map(marker => (
        <TimelineMarkerCard
          key={marker.id}
          marker={marker}
          model={model}
          group={groups.get(marker.groupKey)}
          expanded={Boolean(query.groupKey)}
          selected={selectedId === marker.id}
          onExpandGroup={groupKey =>
            onQueryChange(expandTimelineGroup(query, groupKey))
          }
          onSelect={onSelect}
        />
      ))}
    </ol>
  );
};

const TimelineFooter = ({
  timeline,
  selectedId,
  loadingMore,
  onLoadMore,
}: Pick<TimelineSectionProps, 'timeline' | 'selectedId' | 'onLoadMore'> & {
  loadingMore: boolean;
}) => {
  const classes = useCockpitStyles();
  const { page, status } = timeline;
  if (!page || page.markers.length === 0) return null;
  return (
    <div className={classes.timelineFooter}>
      <Typography variant="body2">
        {page.markers.length} de {page.total} marcos carregados
        {selectedId ? ' · seleção ativa' : ''}.
      </Typography>
      {page.hasMore ? (
        <Button
          variant="outlined"
          disabled={status !== 'ready'}
          onClick={onLoadMore}
        >
          {loadingMore ? 'Carregando…' : 'Carregar mais'}
        </Button>
      ) : (
        <Typography variant="body2">Fim dos registros.</Typography>
      )}
    </div>
  );
};

const TimelineBody = (props: TimelineSectionProps) => {
  const { timeline, model, selectedId, onSelect, onQueryChange, onLoadMore } =
    props;
  const classes = useCockpitStyles();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const loadingMore = isTimelineLoadingMore(timeline);
  const empty =
    timeline.page &&
    timeline.status === 'ready' &&
    timeline.page.markers.length === 0;
  return (
    <>
      <TimelineModeControls
        query={timeline.query}
        onChange={onQueryChange}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen(open => !open)}
      />
      {filtersOpen && (
        <div id="timeline-filters">
          <TimelineFilters query={timeline.query} onChange={onQueryChange} />
        </div>
      )}
      <TimelineLoadState timeline={timeline} loadingMore={loadingMore} />
      <TimelineMarkerList
        timeline={timeline}
        model={model}
        selectedId={selectedId}
        onSelect={onSelect}
        onQueryChange={onQueryChange}
      />
      {empty && (
        <Typography className={classes.timelineState}>
          Nenhum marco corresponde aos filtros selecionados.
        </Typography>
      )}
      <TimelineFooter
        timeline={timeline}
        selectedId={selectedId}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
      />
    </>
  );
};

/**
 * Closed by default: the timeline is the audit trail, not the first thing a
 * reader needs. It opens on its own only when a marker is already selected
 * (a shared link), so the selection is never hidden. Filters stay behind
 * "Filtrar"; the query itself lives in the controller, so a filter carried by
 * the URL applies whether or not this section is open.
 */
export const TimelineSection = (props: TimelineSectionProps) => {
  const classes = useCockpitStyles();
  const [expanded, setExpanded] = useState(() => props.selectedId !== null);
  return (
    <Accordion
      component="section"
      className={classes.panel}
      aria-labelledby="timeline-title"
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        id="timeline-header"
        aria-controls="timeline-content"
        className={classes.collapsibleSummary}
        classes={{
          content: classes.collapsibleSummaryContent,
          expanded: classes.collapsibleExpanded,
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <div>
          <Typography
            id="timeline-title"
            component="h2"
            className={classes.sectionTitle}
          >
            Timeline
          </Typography>
          <Typography variant="body2" className={classes.sectionLede}>
            Marcos gerenciais por padrão, com registros técnicos sob demanda.
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails
        id="timeline-content"
        className={classes.collapsibleDetails}
      >
        <div>
          <TimelineBody {...props} />
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
